"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import { PRODUCT_DOMAIN } from "@/constants";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { EmergencyPhoneNumberProps } from "@/types/confirmation-types";

function EmergencyPhoneNumberContent({
  emergencyContactTitle,
  emergencyContactSubTitle,
  contactNumberOneLabel,
  contactNumberOne,
  contactNumberTwoLabel,
  contactNumberTwo,
  contactNumberThreeLabel,
  contactNumberThree,
  contactNumberFourLabel,
  contactNumberFour,
  callingFromTheUK,
  callingFromOutsideTheUK,
  callingFromMexico,
  callingFromUSAorCanada,
}: Readonly<EmergencyPhoneNumberProps>) {
  const { current } = useWizard();
  const [emergencyAccordionOpen, setEmergencyAccordionOpen] = useState(true);
  function toggleEmergencyAccordion() {
    setEmergencyAccordionOpen(!emergencyAccordionOpen);
  }

  let primaryContactNumber = contactNumberOne;
  let phoneNumBtnStyle =
    "focus:outline-offset-4 focus:outline focus:outline-interactive-focus-default rounded-radius-pill md:mr-2 ml-2 md:ml-0";
  let textStyles =
    "text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold";

  /*
  TODO: Remove this logic with PRODUCT_DOMAIN once this component is refactored .
  */

  if (PRODUCT_DOMAIN === "IRE" || PRODUCT_DOMAIN === "ESP") {
    const regExp = /(?<=\+)\d+(?:\s\d+)*/;
    const match = regExp.exec(contactNumberOne);
    if (match) {
      const [firstMatch] = match;
      primaryContactNumber = `+${firstMatch}`;
    }

    phoneNumBtnStyle += "px-[0]";
    textStyles =
      "text-start text-[18px] font-bold leading-[23px] md:leading-[28px] lg:leading-[23px]";
  }

  const contactDetailsLinesStyle = useMemo(() => {
    if (PRODUCT_DOMAIN === "IRE" || PRODUCT_DOMAIN === "ESP") {
      return "md:flex-col md:items-start";
    }

    return "md:flex-row md:justify-between";
  }, []);

  const isMobile = useMediaQuery("(max-width: 599px)");

  useEffect(() => {
    if (isMobile) {
      setEmergencyAccordionOpen(false);
    } else {
      setEmergencyAccordionOpen(true);
    }
  }, [isMobile]);

  const handleCall = (number: string) => {
    const numberToCall = /\d+/.exec(number.split(" ").join(""));
    const telUri = `tel:${numberToCall?.[0]}`;
    window.open(telUri, "_self");
  };

  return (
    <div
      className="flex flex-col gap-spacing-s md:gap-spacing-m lg:gap-spacing-l"
      data-testid="emergencyPhoneNumberContent-testid"
    >
      <button
        id="emergency-phone-number-accordion-button"
        type="button"
        className="transition duration-300 w-full flex flex-row justify-between items-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-interactive-focus-default "
        onClick={toggleEmergencyAccordion}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            toggleEmergencyAccordion();
          }
        }}
      >
        <h1 className="w-[calc(100%-1rem)] text-left justify-between font-header-font text-text-title text-[23px] md:text-[31px] leading-[27.6px] md:leading-[34.1px] cursor-pointer">
          {emergencyContactTitle}
        </h1>
        {emergencyAccordionOpen ? (
          <ChevronUpIcon
            stroke={selectedColorBrand["interactive-secondary-default"]}
          />
        ) : (
          <ChevronDownIcon
            stroke={selectedColorBrand["interactive-secondary-default"]}
          />
        )}
      </button>
      <div
        className={`space-y-spacing-s md:space-y-spacing-m lg:space-y-spacing-l overflow-hidden transition-opacity duration-500 md:ps-0 pt-2 pb-2 ${
          emergencyAccordionOpen ? "opacity-100" : "opacity-0 hidden"
        }
  `}
      >
        <div>
          <p className="text-2xs font-normal leading-6 text-text-body">
            {emergencyContactSubTitle}
          </p>
        </div>

        <div
          className={`space-y-spacing-xxs md:space-y-0 md:flex ${contactDetailsLinesStyle}`}
        >
          <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle break-words">
            {contactNumberOneLabel}
          </p>
          <button
            id="emergency-phone-number-1"
            type="button"
            className={phoneNumBtnStyle}
            onClick={() =>
              handleCall(
                current.uiElementControls?.isConfirmationNumberBrandImpact &&
                  callingFromOutsideTheUK
                  ? callingFromOutsideTheUK
                  : primaryContactNumber
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    callingFromOutsideTheUK
                    ? callingFromOutsideTheUK
                    : primaryContactNumber
                );
              }
            }}
          >
            <p className={`text-text-title  ${textStyles}`}>
              {current.uiElementControls?.isConfirmationNumberBrandImpact &&
              callingFromOutsideTheUK
                ? callingFromOutsideTheUK
                : contactNumberOne}
            </p>
          </button>
        </div>

        <div
          className={`space-y-spacing-xxs md:space-y-0 md:flex ${contactDetailsLinesStyle}`}
        >
          <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
            {contactNumberTwoLabel}
          </p>
          <button
            id="emergency-phone-number-2"
            type="button"
            className={phoneNumBtnStyle}
            onClick={() =>
              handleCall(
                current.uiElementControls?.isConfirmationNumberBrandImpact &&
                  callingFromTheUK
                  ? callingFromTheUK
                  : contactNumberTwo
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    callingFromTheUK
                    ? callingFromTheUK
                    : contactNumberTwo
                );
              }
            }}
          >
            <p className={`text-text-title ${textStyles}`}>
              {current.uiElementControls?.isConfirmationNumberBrandImpact &&
              callingFromTheUK
                ? callingFromTheUK
                : contactNumberTwo}
            </p>
          </button>
        </div>

        {contactNumberThreeLabel && (
          <div className="space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between">
            <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
              {contactNumberThreeLabel}
            </p>
            <button
              id="emergency-phone-number-3"
              type="button"
              className={phoneNumBtnStyle}
              onClick={() =>
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    callingFromUSAorCanada
                    ? callingFromUSAorCanada
                    : contactNumberThree
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCall(
                    current.uiElementControls
                      ?.isConfirmationNumberBrandImpact &&
                      callingFromUSAorCanada
                      ? callingFromUSAorCanada
                      : contactNumberThree
                  );
                }
              }}
            >
              <p className="text-text-title text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold">
                {current.uiElementControls?.isConfirmationNumberBrandImpact &&
                callingFromUSAorCanada
                  ? callingFromUSAorCanada
                  : contactNumberThree}
              </p>
            </button>
          </div>
        )}
        {contactNumberFourLabel && (
          <div className="space-y-spacing-xxs md:space-y-0 md:flex md:flex-row md:justify-between">
            <p className="text-xs md:text-xs font-normal leading-6 md:leading-[27px] text-text-functional-subtle">
              {contactNumberFourLabel}
            </p>
            <button
              id="emergency-phone-number-4"
              type="button"
              style={{ marginBottom: "8px" }}
              className={phoneNumBtnStyle}
              onClick={() =>
                handleCall(
                  current.uiElementControls?.isConfirmationNumberBrandImpact &&
                    callingFromMexico
                    ? callingFromMexico
                    : contactNumberFour
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleCall(
                    current.uiElementControls
                      ?.isConfirmationNumberBrandImpact && callingFromMexico
                      ? callingFromMexico
                      : contactNumberFour
                  );
                }
              }}
            >
              <p className="text-text-title text-[20px] md:text-[25px] leading-[23px] md:leading-[28px] font-bold">
                {current.uiElementControls?.isConfirmationNumberBrandImpact &&
                callingFromMexico
                  ? callingFromMexico
                  : contactNumberFour}
              </p>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmergencyPhoneNumberContent;
